import React from "react";
import { AlertColor, Autocomplete, TextField, Box } from "@mui/material";
import { AssessmentListItem, DocumentList } from "../../../../../services/types/sessionDetailTypes";
import { useLazyGetAIKeybyDocKeyQuery } from "../../../../../services/cclTokenedSessionApi";
import CclButton from "../../../../../components/common/cclButtons/cclButton";
import CclDrawer from "../../../../../components/common/cclDrawer/cclDrawer";
import CclDrawerActionBar from "../../../../../components/common/cclDrawer/cclDrawerActionBar";
import CclDrawerActionBarButtonGrouping from "../../../../../components/common/cclDrawer/cclDrawerActionBarButtonGrouping";
import CclLoadingButton from "../../../../../components/common/cclButtons/cclLoadingButton";
import useLogAccessEvent from "../../../../../hooks/useLogAccessEvent";
import { AccessEventDocumentAddedToSessionDetails } from "../../../../../services/types/accessEventTypes";

interface AddDocumentDrawerProps {
    open: boolean;
    esKey: number;
    onCancel: () => void;
    onAdd: (newItem: AssessmentListItem) => Promise<void>;
    onViewDocument: (docId: number) => Promise<void>;
    documentOptionsList: DocumentList[];
    showSnackbar: (message: string, severity: AlertColor) => void;
}

const AddDocumentDrawer: React.FC<AddDocumentDrawerProps> = (props) => {
    const { logEvent } = useLogAccessEvent();
    const [addingDocument, setAddingDocument] = React.useState<boolean>(false);
    const [preparingDocumentView, setPreparingDocumentView] = React.useState<boolean>(false);
    const [selectedDocumentValue, setSelectedDocumentValue] = React.useState<string | null>(null);
    const [getDocsAiKey] = useLazyGetAIKeybyDocKeyQuery();

    const textInputSx = {
        "& .MuiOutlinedInput-root": {
            paddingTop: "0px!important",
            paddingBottom: "0px!important",
        },
        "& .MuiInputLabel-root": {
            marginTop: "-8px!important",
        },
        "& .MuiInputLabel-shrink": {
            marginTop: "auto!important",
        },
    };

    const getSelectedDocumentId = (): number => {
        if (props.documentOptionsList == null || props.documentOptionsList.length === 0) return -1;
        const doc = props.documentOptionsList.find((d) => d.documentName === selectedDocumentValue);
        return doc ? doc.documentID : -1;
    };

    const close = () => {
        setSelectedDocumentValue(null);
        props.onCancel();
    };

    const onDocumentChange = (e: any, newValue: any) => {
        setSelectedDocumentValue(newValue ?? null);
    };

    const handleViewDocument = async () => {
        setPreparingDocumentView(true);
        try {
            await props.onViewDocument(getSelectedDocumentId());
        } finally {
            setPreparingDocumentView(false);
        }
    };

    const addDocument = async () => {
        if (selectedDocumentValue) {
            const doc = props.documentOptionsList.filter(
                (d) => d.documentName === selectedDocumentValue,
            )[0];
            if (doc != null) {
                setAddingDocument(true);
                try {
                    let aidata = await getDocsAiKey(doc.documentID).unwrap();

                    if (aidata && aidata?.aI_Key > 0) {
                        let item: AssessmentListItem = {
                            esKey: props.esKey,
                            bPI_Key: 0,
                            bPI_Seq_Num: 1,
                            bPIT_Key: 2,
                            cclMasterKey: doc.documentID,
                            assessmentType: "Document",
                            instrumentTypeKey: 0,
                            cclMasterRaterKey: 0,
                            title: doc.documentName,
                            status: "Add",
                            groupReportRequested: false,
                            aggregateReportRequested: false,
                            aggregateReportAvailable: false,
                            groupReportAvailable: false,
                            docA_Path: "",
                            assetKey: aidata?.aI_Key,
                        };

                        await props.onAdd(item);
                        const evtData: AccessEventDocumentAddedToSessionDetails = {
                            documentId: doc.documentID.toString(),
                            documentTitle: doc.documentName,
                        };
                        logEvent("DocumentAddedToSession", evtData);
                    } else {
                        props.showSnackbar("Assessments & Documents Update Failed", "error");
                    }
                } catch (error) {
                    props.showSnackbar("Assessments & Documents Update Failed", "error");
                } finally {
                    setAddingDocument(false);
                    close();
                }
            }
        }
    };

    return (
        <CclDrawer title={"Add Document(s)"} open={props.open} onClose={close}>
            <Box sx={{ paddingTop: 1 }}>
                <Autocomplete
                    disabled={props.documentOptionsList == null}
                    disablePortal={false}
                    id="combo-document"
                    value={selectedDocumentValue}
                    onChange={(e: any, newValue: any) => onDocumentChange(e, newValue)}
                    options={props.documentOptionsList.map((option) => option.documentName)}
                    sx={{ width: 1 }}
                    renderInput={(params) => (
                        <TextField {...params} sx={textInputSx} label="Select Document" />
                    )}
                    disableClearable={selectedDocumentValue == null}
                />
            </Box>
            <CclDrawerActionBar>
                <CclDrawerActionBarButtonGrouping>
                    <CclLoadingButton
                        disabled={selectedDocumentValue == null}
                        onClick={addDocument}
                        mode={"primary"}
                        loading={addingDocument}
                    >
                        Add
                    </CclLoadingButton>
                    <CclLoadingButton
                        disabled={selectedDocumentValue == null}
                        onClick={handleViewDocument}
                        mode={"primary"}
                        loading={preparingDocumentView}
                    >
                        Download Document
                    </CclLoadingButton>
                </CclDrawerActionBarButtonGrouping>
                <CclButton onClick={close} mode={"secondary"}>
                    Close
                </CclButton>
            </CclDrawerActionBar>
        </CclDrawer>
    );
};

export default AddDocumentDrawer;
